/* Kardhini — transparent header.
   The header sits over the hero and turns solid once the hero has gone by.
   Everything is guarded: if this never runs the header stays transparent
   over the hero, which is still readable, and the CSS keeps it solid
   everywhere else. */
(function () {
  'use strict';

  function init() {
    var header = document.querySelector('[data-k-header-over]');
    if (!header) return;

    var hero = document.querySelector('.k-hero, .k-fh');
    if (!hero) {
      header.classList.add('is-stuck');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      header.classList.add('is-stuck');
      return;
    }

    // Flip once the hero's last sliver has left the top of the screen.
    var io = new IntersectionObserver(
      function (entries) {
        header.classList.toggle('is-stuck', !entries[0].isIntersecting);
      },
      { rootMargin: '-90% 0px 0px 0px', threshold: 0 }
    );
    io.observe(hero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* Kardhini — footer reveal.
   Adds .is-in once the footer comes into view so CSS can run the stagger.
   If this never runs, the reduced-motion guard means the footer is only
   hidden for people who asked for motion, so fall back to showing it. */
(function () {
  'use strict';

  function reveal() {
    var footer = document.querySelector('.k-footer');
    if (!footer) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches || !('IntersectionObserver' in window)) {
      footer.classList.add('is-in');
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        footer.classList.add('is-in');
        io.disconnect();
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    io.observe(footer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reveal, { once: true });
  } else {
    reveal();
  }
})();

/* Kardhini — header: menu drawer and search panel.
   Both are plain hidden elements, so without JS the markup is still in the
   page and the search form still submits. Focus moves into whatever opens,
   stays there while it is open, and returns to the button that opened it. */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), summary, [tabindex]:not([tabindex="-1"])';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function visible(el) {
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  }

  function trap(container, e) {
    if (e.key !== 'Tab') return;
    var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), visible);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Shows a [hidden] element, then adds .is-open on the next frame so the
  // CSS transition has a starting state to run from. Closing reverses it.
  function reveal(el) {
    el.hidden = false;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('is-open'); });
    });
  }

  function conceal(el) {
    el.classList.remove('is-open');
    if (reduced.matches) {
      el.hidden = true;
      return;
    }
    var done = false;
    function finish() {
      if (done || el.classList.contains('is-open')) return;
      done = true;
      el.hidden = true;
    }
    el.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 400);
  }

  function init() {
    var wrap = document.querySelector('[data-k-header]');
    var drawer = document.querySelector('[data-k-drawer]');
    var drawerBtn = document.querySelector('[data-k-drawer-open]');
    var search = document.querySelector('[data-k-search]');
    var searchBtn = document.querySelector('[data-k-search-open]');
    var current = null;

    function close(restoreFocus) {
      if (!current) return;
      var c = current;
      current = null;
      conceal(c.el);
      c.btn.setAttribute('aria-expanded', 'false');
      if (wrap) wrap.classList.remove('is-open');
      document.body.classList.remove('k-locked');
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onOutside, true);
      if (restoreFocus !== false) c.btn.focus();
    }

    function open(el, btn, focusEl, lock) {
      close(false);
      current = { el: el, btn: btn, lock: lock };
      reveal(el);
      btn.setAttribute('aria-expanded', 'true');
      if (wrap) wrap.classList.add('is-open');
      if (lock) document.body.classList.add('k-locked');
      document.addEventListener('keydown', onKey);
      document.addEventListener('click', onOutside, true);
      if (focusEl) setTimeout(function () { focusEl.focus(); }, 50);
    }

    function onKey(e) {
      if (!current) return;
      if (e.key === 'Escape') close();
      else if (current.lock) trap(current.el, e);
    }

    // The search panel closes on a click anywhere outside the header.
    function onOutside(e) {
      if (!current || current.lock) return;
      if (wrap && wrap.contains(e.target)) return;
      close(false);
    }

    if (drawer && drawerBtn) {
      var panel = drawer.querySelector('.k-drawer__panel');
      drawerBtn.addEventListener('click', function () {
        if (current && current.el === drawer) close();
        else open(drawer, drawerBtn, panel, true);
      });
      drawer.querySelectorAll('[data-k-drawer-close]').forEach(function (b) {
        b.addEventListener('click', function () { close(); });
      });
      // Closing on a link means an in-page anchor does not leave it covering the page.
      drawer.addEventListener('click', function (e) {
        if (e.target.closest('a[href]')) close(false);
      });
    }

    if (search && searchBtn) {
      var input = search.querySelector('input[type="search"]');
      searchBtn.addEventListener('click', function () {
        if (current && current.el === search) close();
        else open(search, searchBtn, input, false);
      });
      var closer = search.querySelector('[data-k-search-close]');
      if (closer) closer.addEventListener('click', function () { close(); });
      if (input) live(input, search.querySelector('[data-k-search-results]'));
    }

    // A width change can leave the page locked with nothing open on it.
    window.addEventListener('resize', function () {
      if (!current) document.body.classList.remove('k-locked');
    });
  }

  // Live results from Shopify's predictive search, rendered server-side by
  // sections/predictive-search.liquid so prices come out in shop currency.
  function live(input, results) {
    var url = input.getAttribute('data-k-search-live');
    if (!url || !results) return;
    var timer = null;
    var controller = null;
    var cache = {};

    function render(html) {
      results.innerHTML = html;
      results.hidden = !html;
    }

    function run() {
      var q = input.value.trim();
      if (q.length < 2) {
        render('');
        return;
      }
      if (cache[q] !== undefined) {
        render(cache[q]);
        return;
      }
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      results.setAttribute('aria-busy', 'true');
      fetch(url + '?q=' + encodeURIComponent(q) + '&section_id=predictive-search&resources[limit]=4', {
        signal: controller ? controller.signal : undefined,
      })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('.shopify-section') || doc.body;
          cache[q] = section.innerHTML.trim();
          if (input.value.trim() === q) render(cache[q]);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          render('');
        })
        .finally(function () {
          results.removeAttribute('aria-busy');
        });
    }

    render('');
    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 220);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
